import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../AuthContext'
import { API_BASE } from '../config'

const C = {
  g900:'#0D2218', g800:'#1A3A28', g700:'#235233', g600:'#2D6B42',
  g500:'#3D8A57', g400:'#5AAE73', g300:'#85C99A', g100:'#E8F5EE', g50:'#F2FAF5',
  white:'#FFFFFF', text:'#1A2820', muted:'#5A7065', border:'#C8DDD2', error:'#C0392B',
}

export default function AdminLogin() {
  const { login } = useAuth()
  const navigate = useNavigate()

  const [form, setForm] = useState({ email: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPass, setShowPass] = useState(false)

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }))

  const submit = async () => {
    setError('')
    if (!form.email.trim() || !form.password.trim()) {
      setError('Please enter both email and password.')
      return
    }
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/auth/admin-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email.trim(), password: form.password }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || data.message || 'Invalid credentials')

      // ✅ Force store admin user in localStorage BEFORE calling login
      const adminUser = {
        role: 'admin',
        name: data.user?.name || 'Admin',
        email: data.user?.email || form.email,
        token: data.token,
        refreshToken: data.refreshToken,
        loggedAt: Date.now()
      }
      localStorage.setItem('auth_user', JSON.stringify(adminUser))
      
      // Then update AuthContext
      login('admin', data.user?.name || 'Admin', data.user?.email || form.email, data.token, data.refreshToken)
      
      // Navigate after short delay to let state settle
      setTimeout(() => navigate('/admin/dashboard'), 200)
    } catch (err) {
      setError(err.message || 'Login failed. Check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  const onKey = e => { if (e.key === 'Enter') submit() }

  return (
    <div style={{ minHeight:'100vh', display:'flex', fontFamily:"'DM Sans',sans-serif" }}>

      {/* LEFT PANEL */}
      <div style={{ width:'42%', background:`linear-gradient(145deg,${C.g900},${C.g800},${C.g700})`, display:'flex', flexDirection:'column', justifyContent:'space-between', padding:'3rem', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:`radial-gradient(circle,rgba(255,255,255,.025) 1px,transparent 1px)`, backgroundSize:'26px 26px', pointerEvents:'none' }} />
        <div style={{ position:'absolute', right:'-15%', top:'20%', width:400, height:400, borderRadius:'50%', background:`radial-gradient(circle,rgba(93,174,115,.12) 0%,transparent 65%)`, pointerEvents:'none' }} />

        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:'3rem' }}>
            <div style={{ width:38, height:38, borderRadius:10, background:'rgba(90,174,115,.2)', border:'1px solid rgba(90,174,115,.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="18" height="18" fill="none" stroke={C.g300} strokeWidth="1.8" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            </div>
            <div>
              <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.2rem', color:'white' }}>Revive</div>
              <div style={{ fontSize:'.55rem', color:'rgba(255,255,255,.4)', letterSpacing:'.12em', textTransform:'uppercase' }}>Admin Portal</div>
            </div>
          </div>
          <h1 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'clamp(1.8rem,3vw,2.6rem)', color:'white', lineHeight:1.15, marginBottom:'1rem' }}>
            Secure Admin<br/>Access
          </h1>
          <p style={{ fontSize:'.88rem', color:'rgba(255,255,255,.5)', lineHeight:1.8, maxWidth:280 }}>
            This portal is restricted to authorized Revive administrators only. Unauthorized access is prohibited.
          </p>
        </div>

        <div style={{ position:'relative', zIndex:1 }}>
          {[
            ['🛡️', 'Restricted Access', 'Admin credentials required'],
            ['👥', 'Patient Management', 'View & track all patients'],
            ['🔔', 'Send Reminders', 'Medication & follow-up alerts'],
          ].map(([icon, title, sub]) => (
            <div key={title} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:'1.1rem' }}>
              <div style={{ width:36, height:36, borderRadius:9, background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem', flexShrink:0 }}>{icon}</div>
              <div>
                <div style={{ fontSize:'.82rem', fontWeight:600, color:'rgba(255,255,255,.8)' }}>{title}</div>
                <div style={{ fontSize:'.7rem', color:'rgba(255,255,255,.38)' }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', background:C.g50, padding:'2rem' }}>
        <div style={{ width:'100%', maxWidth:400 }}>
          <div style={{ marginBottom:'2.5rem' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'#FEF9F0', border:'1px solid #FCEDC7', borderRadius:999, padding:'.3rem .85rem', marginBottom:'1.25rem' }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background:'#B7770D', display:'inline-block' }} />
              <span style={{ fontSize:'.68rem', color:'#B7770D', fontWeight:600, letterSpacing:'.1em', textTransform:'uppercase' }}>Admin Only</span>
            </div>
            <h2 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'2rem', color:C.g900, marginBottom:'.4rem' }}>Welcome back</h2>
            <p style={{ fontSize:'.85rem', color:C.muted }}>Sign in with your admin credentials to continue.</p>
          </div>

          {error && (
            <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'.75rem 1rem', marginBottom:'1.25rem', fontSize:'.82rem', color:C.error, display:'flex', alignItems:'center', gap:8 }}>
              ⚠️ {error}
            </div>
          )}

          <div style={{ display:'flex', flexDirection:'column', gap:'1rem', marginBottom:'1.5rem' }}>
            <div>
              <label style={{ fontSize:'.68rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', display:'block', marginBottom:'.4rem' }}>Email Address</label>
              <input
                name="email" type="email" value={form.email}
                onChange={handle} onKeyDown={onKey}
                placeholder="admin@reviveskin.in"
                style={{ width:'100%', padding:'.75rem 1rem', border:`1.5px solid ${C.border}`, borderRadius:10, fontSize:'.88rem', outline:'none', fontFamily:"'DM Sans',sans-serif", color:C.text, background:'white', boxSizing:'border-box', transition:'border-color .2s' }}
                onFocus={e => e.target.style.borderColor = C.g500}
                onBlur={e => e.target.style.borderColor = C.border}
              />
            </div>
            <div>
              <label style={{ fontSize:'.68rem', fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'.08em', display:'block', marginBottom:'.4rem' }}>Password</label>
              <div style={{ position:'relative' }}>
                <input
                  name="password" type={showPass ? 'text' : 'password'} value={form.password}
                  onChange={handle} onKeyDown={onKey}
                  placeholder="••••••••"
                  style={{ width:'100%', padding:'.75rem 2.75rem .75rem 1rem', border:`1.5px solid ${C.border}`, borderRadius:10, fontSize:'.88rem', outline:'none', fontFamily:"'DM Sans',sans-serif", color:C.text, background:'white', boxSizing:'border-box', transition:'border-color .2s' }}
                  onFocus={e => e.target.style.borderColor = C.g500}
                  onBlur={e => e.target.style.borderColor = C.border}
                />
                <button type="button" onClick={() => setShowPass(s => !s)}
                  style={{ position:'absolute', right:'.75rem', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:C.muted, fontSize:'.85rem', padding:0 }}>
                  {showPass ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
          </div>

          <button onClick={submit} disabled={loading}
            style={{ width:'100%', padding:'.9rem', borderRadius:11, background: loading ? C.g600 : C.g700, color:'white', border:'none', fontSize:'.9rem', fontWeight:600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily:"'DM Sans',sans-serif", letterSpacing:'.04em', display:'flex', alignItems:'center', justifyContent:'center', gap:9, transition:'all .2s', boxShadow:`0 4px 16px rgba(35,82,51,.25)` }}>
            {loading
              ? <><span style={{ width:16, height:16, borderRadius:'50%', border:'2px solid rgba(255,255,255,.3)', borderTopColor:'white', display:'inline-block', animation:'spin .8s linear infinite' }} /> Signing in…</>
              : '🔐 Sign in as Admin'
            }
          </button>

          <div style={{ textAlign:'center', marginTop:'1.5rem' }}>
            <a href="/" style={{ fontSize:'.8rem', color:C.muted, textDecoration:'none' }}
              onMouseEnter={e => e.target.style.color = C.g700}
              onMouseLeave={e => e.target.style.color = C.muted}>
              ← Back to Revive
            </a>
          </div>

          <div style={{ marginTop:'2rem', padding:'.85rem 1rem', background:'white', border:`1px solid ${C.border}`, borderRadius:10, display:'flex', gap:10, alignItems:'flex-start' }}>
            <span style={{ fontSize:'1rem', flexShrink:0 }}>🔒</span>
            <div style={{ fontSize:'.72rem', color:C.muted, lineHeight:1.6 }}>
              This page is not linked from the public site. Only share this URL with authorized staff. All admin actions are logged.
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing:border-box; }
        @keyframes spin { to { transform: rotate(360deg) } }
      `}</style>
    </div>
  )
}
